import * as Sentry from '@sentry/react';
import { Navigate, RouteObject, useLocation } from 'react-router-dom';

import { useDefaultTeamSlug } from 'core/support/useDefaultTeamSlug';
import { useAuthUserContext } from 'support/auth/AuthUserContext';
import { notEmpty } from 'support/filters';
import { UserSegment } from 'types/graphql';

export const redirectRoutes: RouteObject[] = [
  {
    index: true,
    element: (
      <LearnerGuard>
        <RootPathRedirect />
      </LearnerGuard>
    ),
  },
  {
    path: 'inspiration-library',
    Component: InspirationLibraryRedirect,
  },

  // Required until addInvitePrefix is fixed
  ...[
    'study-room/invites/:inviteID/:teamSlug/tracks/:trackSlug',
    'study-room/invites/:inviteID/:teamSlug/tracks/:trackSlug/*',
    'study-room/invites/:inviteID',
  ].map(path => ({
    path,
    lazy: () =>
      import('./core/pages/study-room-link-redirect/StudyRoomLinkRedirect').then(m => ({
        Component: m.StudyRoomLinkRedirect,
      })),
  })),

  // Temporary to ensure /manage migration goes smoothly
  ...[
    ':teamSlug',
    ':teamSlug/api-keys',
    ':teamSlug/insights',
    ':teamSlug/inspiration-library',
    ':teamSlug/limits',
    ':teamSlug/members',
    ':teamSlug/sandboxes',
    ':teamSlug/secrets',
    ':teamSlug/sso',
    ':teamSlug/statistics',
    ':teamSlug/tags',
    ':teamSlug/plays-report',
    ':teamSlug/users-report',
    ':teamSlug/webhooks',
    ':teamSlug/arcades/*',
    ':teamSlug/hot-start/*',
    ':teamSlug/invites/*',
    ':teamSlug/sandbox-presets/*',
    ':teamSlug/tracks/:trackSlug/dashboard/*',
  ].map<RouteObject>(path => ({
    path,
    Component: ManagePathRedirect,
  })),
];

export function LearnerGuard({ children }: { children: React.ReactNode }) {
  const { instruqtUser } = useAuthUserContext();

  return instruqtUser.segments.includes(UserSegment.Learner) ? <Navigate to="/study-room" replace /> : <>{children}</>;
}

function RootPathRedirect() {
  const { defaultTeamSlug } = useRedirectDefaultTeamSlug();

  return <Navigate to={`/manage/${defaultTeamSlug}`} replace />;
}

function InspirationLibraryRedirect() {
  const { defaultTeamSlug } = useRedirectDefaultTeamSlug();

  return <Navigate to={`/manage/${defaultTeamSlug}/inspiration-library`} replace />;
}

function ManagePathRedirect() {
  const { pathname, search, hash, state } = useLocation();

  Sentry.captureMessage('/manage redirect', { level: 'info', tags: { pathname } });

  return <Navigate to={{ pathname: `/manage${pathname}`, hash, search }} state={state} replace />;
}

export function useRedirectDefaultTeamSlug() {
  const {
    instruqtUser: { teams },
  } = useAuthUserContext();
  const availableTeams = teams.map(e => e.node).filter(notEmpty);
  const defaultTeamSlug = useDefaultTeamSlug(availableTeams);

  return { defaultTeamSlug };
}
